import { useEffect } from 'react';

import { useBidding } from 'Context/BiddingContext';
import type { VehicleData } from 'Pages/vehicles/helpers/vehicle.schema';
import { RealtimeBiddingDataSchema } from 'Pages/vehicles/schemas/realtimeBidding.schema';
import { logZodValidationError } from 'Services/zod.helpers';
import useUser from 'Utilities/hooks/useUser';

import { mapRealtimeBiddingDataToVehicleData } from './mappers/mapRealtimeBiddingDataToVehicleData';

export const useRealtimeBiddingData = (vehicleData: VehicleData): VehicleData => {
	const { clearVehicleIds, subscribeVehicleId, vehiclesBidData } = useBidding();
	const user = useUser();

	const { id, state } = vehicleData;

	useEffect(() => {
		subscribeVehicleId({ id, stateSlug: state.slug }, { append: false });

		return () => {
			clearVehicleIds();
		};
	}, [clearVehicleIds, id, state.slug, subscribeVehicleId]);

	const currentRealtimeVehicleBiddingData = vehiclesBidData?.[id];

	if (!currentRealtimeVehicleBiddingData) {
		return vehicleData;
	}

	const parsedRealtimeBiddingData = RealtimeBiddingDataSchema.safeParse(currentRealtimeVehicleBiddingData);

	if (parsedRealtimeBiddingData.success) {
		// In the future it would be good to change our websocket to match our data structure so that this is not needed
		return mapRealtimeBiddingDataToVehicleData({
			realtimeBiddingData: parsedRealtimeBiddingData.data,
			user,
			vehicleData,
		});
	}

	logZodValidationError({ error: parsedRealtimeBiddingData.error, functionName: 'useRealtimeBiddingData' });

	return vehicleData;
};
